import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Form, Row, Col } from "react-bootstrap";
import PageLoading from "components/PageLoading";
import "./ExtractAnalysis.css";
import BonusRangeTable from "../ComponentsExtractAnalysis/RangeTable/BonusRangeTable";
import BonusDocumentTable from "../ComponentsExtractAnalysis/DocumentTable/BonusDocumentTable";
import GenericSmallTable from "../ComponentsExtractAnalysis/GenericTable/GenericSmallTable";
import DeducaoDocumentTable from "../ComponentsExtractAnalysis/DeducaoTable/DeducaoTable";
import ExtracActions from "../ComponentsExtractAnalysis/ExtractActions/ExtractActions";
import SupportTable from "../ComponentsExtractAnalysis/SupportTable/SupportTable";
import ValoresJaSolicitadosTable from "../ComponentsExtractAnalysis/ValoresJaSolicitadosTable/ValoresJaSolicitadosTable";
import {
  mapExtratoSolicitacaoApiToDeducoes,
  mapExtratoSolicitacaoApiToPreview,
  mapExtratoSolicitacaoApiToValoresSolicitados,
  hasDeducaoAtrasoPreenchida,
  type ValoresSolicitadosView,
} from "../V2/mapExtratoSolicitacaoApi";
import { buildPatchExtratoAnalisePayload } from "../V2/buildPatchExtratoAnalisePayload";
import { gerarPdfExtratoBlob } from "../V2/gerarPdfExtrato";
import ModalExtractVisualizacao from "../ComponentsExtractAnalysis/ModalExtractVisualizacao/ModalExtractVisualizacao";
import ModalConfirmEnvioExtrato from "../ComponentsExtractAnalysis/ModalConfirmEnvioExtrato/ModalConfirmEnvioExtrato";
import {
  salvarExtrato,
  salvarExtratoAnalise,
  enviarExtrato,
  enviarExtratoSolicitacao,
  buildPreviewExtrato,
  getDeducoesExtrato,
  getExtratoSolicitacaoById,
} from "../../../../services/ExtractService";
import { getSindicatoById } from "../../../../services/sindicatoService";
import { getHistoricoAnalise } from "../../../../services/analiseService";
import { FaArrowLeft } from "react-icons/fa";
import { getQuestionarioRespostaById } from "../../../../services";

import ExtractModel from "../ExtractModel/ExtractModel";
import NotificationToast from "../../../../components/NotificationToast/NotificationToast";
import {
  type DadosDeducao,
  MSG_OUTRAS_DEDUCOES_EXCEDEM_BRUTO,
  MSG_OUTRAS_DEDUCOES_INVALIDAS,
  outrasDeducoesExcedemBruto,
  validarOutrasDeducoes,
} from "../ComponentsExtractAnalysis/DeducaoTable/DeducaoTable";
import { PATHS } from "../../../../routes/paths";

interface DadosSindicato {
  id?: string;
  nome?: string;
  cnpj?: string;
  sigla?: string;
  receitaSuperiorDozeSalarios?: boolean;
  [key: string]: unknown;
}

interface PreviewExtrato {
  valorMinimo?: number;
  valorBonus?: number;
  valorBonusExtra?: number;
  valorTotalApoioBruto?: number;
  receitaOperacional?: number;
  faixaReceitaBonus?: {
    titulo?: string;
    receitaMin?: number;
    receitaMax?: number;
    valorMinimo?: number;
    valorBonusPotencial?: number;
    valorMaximoPotencial?: number;
  };
  criterioBonus?: Array<{ documento?: string; nome?: string; conforme?: boolean; percentual?: number; valor?: string | number }>;
  criterioBonusExtra?: Array<{ documento?: string; nome?: string; conforme?: boolean; percentual?: number; valor?: string | number }>;
  [key: string]: unknown;
}

const CRITERIO_MAP: Record<string, number> = {
  "plano-de-negocio-sindical": 1,
  "renovacao-da-lideranca": 2,
  "balanco-patrimonial": 3,
  "relatorio-de-atividades": 4,
  "eventos-promovidos-pelo-sindicato": 5,
  "utilizacao-total-do-sistema-hi-gestor": 6,
  "evolucao-receita-operacional": 7,
  "80-de-retencao-de-associados": 8,
  "evolucao-do-associativismo": 9,
  "acao-internacional": 10,
  "defesa-de-interesse-implementada-no-executivo-e-legislativo": 11,
  "indicacao-de-membros-para-o-programa-fiemg-jovem": 12,
};

const ExtractAnalysisV1 = () => {
  const { idSindicato, idResposta: idQuestionario } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = (location.state ?? {}) as {
    selectedYear?: number;
    solicitacao?: string;
  };
  const selectedYear = routeState.selectedYear ?? null;
  const extratoV2 = Number(selectedYear) > 2025;

  const [dadosSindicato, setDadosSindicato] = useState<DadosSindicato>({});
  const [valoresSolicitados, setValoresSolicitados] =
    useState<ValoresSolicitadosView | null>(null);
  const [solicitacaoLabel, setSolicitacaoLabel] = useState(
    routeState.solicitacao ?? "",
  );
  const [previewData, setPreviewData] = useState<PreviewExtrato | null>(null);
  const [deducaoData, setDeducaoData] = useState<DadosDeducao | null>(null);
  const deducaoDataRef = useRef<DadosDeducao | null>(null);
  const [mostrarErrosOutras, setMostrarErrosOutras] = useState(false);
  const fetchExtratoDoneRef = useRef(false);

  const [quantidadeAdimplentes, setQuantidadeAdimplentes] = useState(0);
  const [receita, setReceita] = useState<unknown[]>([]);
  const [associado, setAssociado] = useState<unknown[]>([]);

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [showModalExtract, setShowModalExtract] = useState(false);
  const [showModalConfirm, setShowModalConfirm] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const [toastData, setToastData] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const extractRef = useRef<HTMLDivElement | null>(null);

  const handleCloseModal = () => setShowModalExtract(false);

  const handleClose = () => {
    navigate(PATHS.DOC_EXTRACT);
  };

  const handleEnviar = async () => {
    setEnviando(true);
    try {
      const el = extractRef.current;
      if (!el) return;

      const pdfWidth = Math.max(el.scrollWidth, 1200);
      const clone = el.cloneNode(true) as HTMLElement;
      clone.style.cssText = `position:fixed;left:-9999px;top:0;width:${pdfWidth}px;background:#fff;`;
      document.body.appendChild(clone);
      let blob: Blob;
      try {
        blob = await gerarPdfExtratoBlob(clone);
      } finally {
        document.body.removeChild(clone);
      }

      const arquivo = new File([blob], "extrato.pdf", {
        type: "application/pdf",
      });

      const sucesso = extratoV2
        ? await enviarExtratoSolicitacao({
            solicitacaoAvaliacaoId: idQuestionario!,
            arquivo,
            pdfResumo: blob,
          })
        : await enviarExtrato({ idSindicato: idSindicato!, arquivo });

      if (sucesso) {
        setToastData({
          show: true,
          message: "Extrato enviado com sucesso!",
          type: "success",
        });
        setShowModalConfirm(false);
        setShowModalExtract(false);
      } else {
        setToastData({
          show: true,
          message: "Falha no envio do extrato.",
          type: "warning",
        });
        setShowModalConfirm(false);
        setShowModalExtract(false);
      }
    } catch (e) {
      setToastData({
        show: true,
        message: "Erro ao gerar ou enviar o PDF.",
        type: "danger",
      });
    } finally {
      setEnviando(false);
    }
  };

  const abrirModalVisualizacao = async () => {
    const resultado = await handleSalvarExtrato();

    if (resultado?.type === "success") {
      if (previewData && deducaoData && dadosSindicato) {
        setShowModalExtract(true);
      } else {
        alert("Ainda carregando os dados necessários para visualização.");
      }
    } else {
      alert("Erro ao salvar o extrato. Tente novamente.");
    }
  };

  useEffect(() => {
    if (extratoV2) return;

    const fetchSindicato = async () => {
      try {
        const response = await getSindicatoById(idSindicato);
        if (response?.data && Object.keys(response.data).length > 0) {
          setDadosSindicato(response.data);
        } else {
          throw new Error("Dados de sindicato não encontrados ou vazios.");
        }
      } catch (e) {
        console.error(e);
        setErro("Erro ao carregar dados do sindicato.");
      }
    };

    if (idSindicato) fetchSindicato();
  }, [idSindicato, extratoV2]);

  useEffect(() => {
    fetchExtratoDoneRef.current = false;
  }, [idSindicato, idQuestionario, extratoV2]);

  const sindicatoId = (dadosSindicato as DadosSindicato)?.id;

  useEffect(() => {
    const fetchDadosExtratoV2 = async () => {
      setCarregando(true);
      try {
        const detalhe = await getExtratoSolicitacaoById(idQuestionario!);
        if (!detalhe) {
          throw new Error("Extrato não encontrado.");
        }

        setDadosSindicato({
          nome: detalhe.dadosSindicato?.nome ?? "",
          cnpj: detalhe.dadosSindicato?.cnpj ?? "",
          sigla: detalhe.dadosSindicato?.sigla ?? "",
        });
        setPreviewData(mapExtratoSolicitacaoApiToPreview(detalhe));
        const deducoes = mapExtratoSolicitacaoApiToDeducoes(detalhe);
        setDeducaoData(deducoes);
        deducaoDataRef.current = deducoes;
        setValoresSolicitados(
          mapExtratoSolicitacaoApiToValoresSolicitados(detalhe),
        );
        if (!solicitacaoLabel && detalhe.valoresJaSolicitadosExtrato?.solicitacoes?.[0]) {
          setSolicitacaoLabel(
            detalhe.valoresJaSolicitadosExtrato.solicitacoes[0].solicitacao ??
              "1ª Solicitação",
          );
        }
      } catch (e) {
        console.error("Erro ao carregar extrato V2:", e);
        setErro("Erro ao carregar dados do extrato.");
      } finally {
        setCarregando(false);
      }
    };

    const fetchDadosExtrato = async () => {
      setCarregando(true);

      try {
        const respostaService =
          await getQuestionarioRespostaById(idQuestionario);

        const EvoReceita = respostaService.data.evolucaoReceitaOperacional;
        const EvoAssociado = respostaService.data.evolucaoAssociados;
        setReceita(EvoReceita || []);
        setAssociado(EvoAssociado || []);

        const rawJson = respostaService.data.json;
        const parsedJson =
          typeof rawJson === "string" ? JSON.parse(rawJson) : rawJson;
        const temas = parsedJson?.docsObrigatorios?.[0]?.tema || [];

        const temaAdimplencia = temas.find((t) =>
          (t.titulo || "").includes(
            "ADIMPLÊNCIA FINANCEIRA DAS EMPRESAS ASSOCIADAS",
          ),
        );

        const criterioQuantas = temaAdimplencia?.criterio?.find((c) =>
          (c.titulo || "").includes("Quantas empresas"),
        );

        const respostaQuantas = criterioQuantas?.campo?.find(
          (c) => c.idCampo === "quantas-empresa",
        )?.resposta;

        const qtd = Number(respostaQuantas) || 0;
        setQuantidadeAdimplentes(qtd);

        const historicoA = await getHistoricoAnalise(idQuestionario);

        const historicoArray =
          historicoA?.historicoAnaliseAdequacaoCampoQuestionarioJson || [];

        const criteriosBonusQuestionario = Array.from(
          historicoArray
            .reduce((map, item) => {
              const idCriterioBonus = CRITERIO_MAP[item.idCampoJson];
              if (!idCriterioBonus) return map;

              const conforme = item.conforme == null ? false : !!item.conforme;
              map.set(idCriterioBonus, { idCriterioBonus, conforme });
              return map;
            }, new Map())
            .values(),
        );

        const payload = {
          idQuestionario: parseInt(idQuestionario ?? "0", 10),
          quantidadeAdimplentes: qtd,
          receitaSuperiorDozeSalarios:
            (dadosSindicato as DadosSindicato)?.receitaSuperiorDozeSalarios ?? false,
          criteriosBonusQuestionario,
        };

        const preview = await buildPreviewExtrato(payload);
        setPreviewData(preview);

        const deducoes = await getDeducoesExtrato(idSindicato);
        setDeducaoData(deducoes);
        deducaoDataRef.current = deducoes;
      } catch (e) {
        console.error("Erro ao carregar dados do extrato:", e);
        setErro("Erro ao carregar dados do extrato.");
      } finally {
        setCarregando(false);
      }
    };

    if (!idQuestionario) return;

    if (extratoV2) {
      if (fetchExtratoDoneRef.current) return;
      fetchExtratoDoneRef.current = true;
      fetchDadosExtratoV2();
      return;
    }

    if (!idSindicato || !sindicatoId) return;
    if (fetchExtratoDoneRef.current) return;
    fetchExtratoDoneRef.current = true;
    fetchDadosExtrato();
  }, [
    idSindicato,
    idQuestionario,
    (dadosSindicato as DadosSindicato)?.id,
    extratoV2,
  ]);

  const atualizarDeducoes = useCallback((dados: DadosDeducao) => {
    setDeducaoData(dados);
    deducaoDataRef.current = dados;
  }, []);

  const handleFinalizar = async () => {
    const result = await handleSalvarExtrato();

    if (result?.type === "success") {
      if (extratoV2) {
        setShowModalConfirm(true);
      } else if (previewData && deducaoData && dadosSindicato) {
        setShowModalExtract(true);
      } else {
        setToastData({
          show: true,
          message:
            "Ainda carregando os dados necessários para visualização.",
          type: "warning",
        });
      }
      return result;
    }

    if (result?.show) {
      setToastData({
        show: true,
        message: result.message ?? "Erro ao salvar o extrato. Tente novamente.",
        type: result.type ?? "danger",
      });
    } else {
      console.warn("Falha ao salvar antes de gerar o extrato.");
    }

    return result;
  };

  const handleSalvarExtrato = async () => {
    // Valida antes do loading: a tabela fica montada e mostra os campos em vermelho.
    const outrasInvalidas = validarOutrasDeducoes(
      deducaoDataRef.current?.deducaoOutros,
    );
    if (Object.keys(outrasInvalidas).length > 0) {
      setMostrarErrosOutras(true);
      setToastData({
        show: true,
        message: MSG_OUTRAS_DEDUCOES_INVALIDAS,
        type: "error",
      });
      return {
        show: true,
        message: MSG_OUTRAS_DEDUCOES_INVALIDAS,
        type: "danger",
      };
    }

    if (
      outrasDeducoesExcedemBruto(
        deducaoDataRef.current?.deducaoOutros,
        previewData?.valorTotalApoioBruto,
      )
    ) {
      setToastData({
        show: true,
        message: MSG_OUTRAS_DEDUCOES_EXCEDEM_BRUTO,
        type: "error",
      });
      return {
        show: true,
        message: MSG_OUTRAS_DEDUCOES_EXCEDEM_BRUTO,
        type: "danger",
      };
    }

    setCarregando(true);

    try {
      const dadosParaSalvar = deducaoDataRef.current;

      if (!dadosParaSalvar) {
        return {
          show: true,
          message: "Dados incompletos para salvar o extrato.",
          type: "danger",
        };
      }

      if (extratoV2) {
        if (!idQuestionario) {
          return {
            show: true,
            message: "Solicitação não identificada para salvar o extrato.",
            type: "danger",
          };
        }

        const payload = buildPatchExtratoAnalisePayload(dadosParaSalvar);

        const sucesso = await salvarExtratoAnalise(idQuestionario, payload);

        if (sucesso) {
          const detalhe = await getExtratoSolicitacaoById(idQuestionario);
          if (detalhe) {
            const novasDeducoes = mapExtratoSolicitacaoApiToDeducoes(detalhe);
            setDeducaoData(novasDeducoes);
            deducaoDataRef.current = novasDeducoes;
            setPreviewData(mapExtratoSolicitacaoApiToPreview(detalhe));
            setValoresSolicitados(
              mapExtratoSolicitacaoApiToValoresSolicitados(detalhe),
            );
          }
        }

        return {
          show: true,
          message: sucesso
            ? "Extrato salvo com sucesso."
            : "Erro ao salvar extrato.",
          type: sucesso ? "success" : "danger",
          reload: sucesso,
        };
      }

      if (!idSindicato) {
        return {
          show: true,
          message: "Dados incompletos para salvar o extrato.",
          type: "danger",
        };
      }

      const existentes = deducaoDataRef.current?.deducaoTitulos || [];

      const makeKey = (t: { filial?: string; titulo?: string }) =>
        `${String(t.filial || "")
          .trim()
          .toUpperCase()}|${String(t.titulo || "").trim()}`;

      const indexByKey = new Map(
        existentes
          .filter((x) => Number(x?.id) > 0)
          .map((x) => [makeKey(x), Number(x.id)]),
      );

      const deducaoAtrasoValida =
        dadosParaSalvar?.deducaoAtraso &&
          dadosParaSalvar?.deducaoAtraso?.deduzir
          ? dadosParaSalvar?.deducaoAtraso
          : null;

      const deducaoTitulosTratados = (dadosParaSalvar?.deducaoTitulos || [])
        .filter((t) => t?.titulo && typeof t?.valor !== "undefined")
        .map((t) => {
          const base = {
            filial: (t.filial || "").trim(),
            titulo: String(t.titulo || "").trim(),
            natureza: String(t.natureza || "").trim(),
            dataEmissao: t.dataEmissao ?? null,
            dataVencimento: t.dataVencimento ?? null,
            atrasoDias: Number(t.atrasoDias) || 0,
            valor: Number(t.valor) || 0,
            historico: t.historico || "",
            deduzir: !!t.deduzir,
          };

          const idNum = Number(t.id);
          if (idNum > 0) return { ...base, id: idNum };

          const key = makeKey(base);
          const existingId = indexByKey.get(key);
          return existingId ? { ...base, id: existingId } : base;
        });

      const deducaoOutrosTratados = (dadosParaSalvar?.deducaoOutros || [])
        .filter(
          (o) => o?.descricao?.trim() !== "" && typeof o?.valor !== "undefined",
        )
        .map((o) => ({
          ...o,
          deduzir: !!o.deduzir,
        }));

      const payload = {
        idSindicato: parseInt(idSindicato),
        deducaoAtraso: deducaoAtrasoValida || {},
        deducoesTitulos: deducaoTitulosTratados,
        deducoesOutros: deducaoOutrosTratados,
      };

      const sucesso = await salvarExtrato(payload);

      if (sucesso) {
        const novasDeducoes = await getDeducoesExtrato(idSindicato);
        setDeducaoData(novasDeducoes);
        deducaoDataRef.current = novasDeducoes;
      }

      return {
        show: true,
        message: sucesso
          ? "Extrato salvo com sucesso."
          : "Erro ao salvar extrato.",
        type: sucesso ? "success" : "danger",
        reload: sucesso,
      };
    } catch (e) {
      console.error("❌ Erro ao salvar extrato:", e);
      return {
        show: true,
        message: "Erro inesperado ao salvar o extrato.",
        type: "danger",
      };
    } finally {
      setCarregando(false);
    }
  };

  if (carregando) return <PageLoading />;

  if (erro) return <p className="text-danger">Erro: {erro}</p>;

  const colunaConforme = extratoV2 ? "CONFORME?" : "CUMPRIU?";
  const titleBonus: [string, string, string, string] = [
    "DOCUMENTO BÔNUS",
    colunaConforme,
    "PERCENTUAL",
    "VALOR",
  ];
  const titleBonusExtra: [string, string, string, string] = [
    "DOCUMENTO BÔNUS EXTRA",
    colunaConforme,
    "PERCENTUAL",
    "VALOR",
  ];

  const dadosResumoApoio = [
    {
      label: "VALOR MÍNIMO",
      valor:
        previewData?.valorMinimo?.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        }) || "R$ 0,00",
    },
    {
      label: "BÔNUS ALCANÇADO",
      valor:
        previewData?.valorBonus?.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        }) || "R$ 0,00",
    },
    {
      label: "BÔNUS EXTRA ALCANÇADO",
      valor:
        previewData?.valorBonusExtra?.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        }) || "R$ 0,00",
    },
    {
      label: "VALOR TOTAL DO APOIO",
      valor:
        previewData?.valorTotalApoioBruto?.toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        }) || "R$ 0,00",
    },
  ];

  const formatCNPJ = (cnpj: string | undefined) => {
    if (!cnpj) return "";
    const onlyDigits = cnpj.toString().replace(/\D/g, "");
    return onlyDigits.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      "$1.$2.$3/$4-$5",
    );
  };

  return (
    <Container
      className="analysis-container py-4"
      style={{ position: "relative" }}
    >
      <div
        className="merit-voltar-seta"
        style={{
          position: "absolute",
          top: "20px",
          right: "30px",
          zIndex: 10,
        }}
        onClick={() => navigate(PATHS.DOC_EXTRACT)}
        title="Voltar para a lista"
      >
        <FaArrowLeft />
      </div>

      <div className="extract-analysis-header d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <h1 className="extract-title mb-0">EXTRATO</h1>
        {extratoV2 && selectedYear ? (
          <span className="extract-solicitacao-ref text-muted">
            {solicitacaoLabel || "Solicitação"} - {selectedYear}
          </span>
        ) : null}
      </div>

      <div style={{ display: "none" }}>
        <div ref={extractRef}>
          <ExtractModel
            previewMode={true}
            idResposta={idQuestionario}
            idSindicato={idSindicato}
            previewData={previewData}
            dadosSindicato={dadosSindicato}
            deducaoData={deducaoData}
            quantidadeAdimplentes={quantidadeAdimplentes}
            receita={receita}
            associado={associado}
            dadosValor={dadosResumoApoio}
          />
        </div>
      </div>

      <Form>
        <Form.Group className="mb-3">
          <Form.Label className="extract-label">NOME DO SINDICATO</Form.Label>
          <Form.Control
            type="text"
            value={dadosSindicato?.nome || ""}
            disabled
          />
        </Form.Group>

        <Row className="gy-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label className="extract-label">
                CNPJ DO SINDICATO
              </Form.Label>
              <Form.Control
                type="text"
                value={formatCNPJ(dadosSindicato?.cnpj || "")}
                disabled
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label className="extract-label">SIGLA</Form.Label>
              <Form.Control
                type="text"
                value={dadosSindicato?.sigla || ""}
                disabled
              />
            </Form.Group>
          </Col>
        </Row>
      </Form>

      <BonusRangeTable
        tituloFaixa={previewData?.faixaReceitaBonus?.titulo}
        receitaMin={previewData?.faixaReceitaBonus?.receitaMin}
        receitaMax={previewData?.faixaReceitaBonus?.receitaMax}
        valorMinimo={previewData?.faixaReceitaBonus?.valorMinimo}
        valorBonusPotencial={
          previewData?.faixaReceitaBonus?.valorBonusPotencial
        }
        valorMaximoPotencial={
          previewData?.faixaReceitaBonus?.valorMaximoPotencial
        }
      />

      <Row className="valor-receber">
        <Col md={6}>
          <h3 className="receita-title">VALOR A RECEBER</h3>
          <Form.Group>
            <Form.Label className="extract-label">
              RECEITA OPERACIONAL
            </Form.Label>
            <Form.Control
              type="text"
              value={
                previewData?.receitaOperacional != null
                  ? Number(previewData.receitaOperacional).toLocaleString(
                    "pt-BR",
                    {
                      style: "currency",
                      currency: "BRL",
                    },
                  )
                  : "R$ 0,00"
              }
              disabled
            />
          </Form.Group>
        </Col>
      </Row>

      <div className="bonus-tables-wrapper">
        <BonusDocumentTable
          data={previewData?.criterioBonus || []}
          title={titleBonus}
        />
        <BonusDocumentTable
          data={previewData?.criterioBonusExtra || []}
          title={titleBonusExtra}
        />
      </div>

      <GenericSmallTable dados={dadosResumoApoio} corValor="verde" />

      <DeducaoDocumentTable
        atrasoDocs={
          hasDeducaoAtrasoPreenchida(deducaoData?.deducaoAtraso)
            ? [deducaoData.deducaoAtraso]
            : []
        }
        titulosEmAberto={deducaoData?.deducaoTitulos || []}
        outrasDeducoes={deducaoData?.deducaoOutros || []}
        totalBrutoBonus={
          previewData?.valorTotalApoioBruto?.toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
          }) || "R$ 0,00"
        }
        onUpdateDeducoes={atualizarDeducoes}
        mostrarErrosOutras={mostrarErrosOutras}
        disabled={!extratoV2}
      />

      {extratoV2 ? (
        valoresSolicitados ? (
          <ValoresJaSolicitadosTable
            itens={valoresSolicitados.itens}
            total={valoresSolicitados.total}
            valorLiquidoApoio={valoresSolicitados.valorLiquidoApoio}
          />
        ) : null
      ) : (
        <SupportTable
          dadosValoresLiberados={[]}
          totalValoresLiberados="R$0,00"
          pendenteLiberacao="R$0,00"
          liquidoApoio="R$0,00"
        />
      )}

      {extratoV2 && (
        <ExtracActions
          onSalvar={handleSalvarExtrato}
          onFinalizar={handleFinalizar}
          idSindicato={idSindicato}
          onVisualizar={abrirModalVisualizacao}
        />
      )}

      <ModalExtractVisualizacao
        show={showModalExtract}
        onClose={handleCloseModal}
        onSolicitarConfirmacaoEnvio={() => {
          setShowModalExtract(false);
          setTimeout(() => setShowModalConfirm(true), 200);
        }}
        idSindicato={idSindicato}
        idResposta={idQuestionario}
        previewData={previewData}
        deducaoData={deducaoData}
        dadosSindicato={dadosSindicato}
        quantidadeAdimplentes={quantidadeAdimplentes}
        receita={receita}
        associado={associado}
      />

      <ModalConfirmEnvioExtrato
        show={showModalConfirm}
        onClose={() => {
          setShowModalConfirm(false);
          if (!extratoV2) {
            setShowModalExtract(true);
          }
        }}
        onConfirm={handleEnviar}
        enviando={enviando}
      />

      <NotificationToast
        show={toastData.show}
        onClose={() => setToastData((prev) => ({ ...prev, show: false }))}
        message={toastData.message}
        type={toastData.type}
      />
    </Container>
  );
};

export default ExtractAnalysisV1;
